import { useId, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import {
  Button,
  Dialog,
  Input,
  Label,
  Loader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@repo/ui";
import { messageErreur } from "@repo/scorecard-editor";

/**
 * Donne à l'organisation une copie indépendante d'un modèle global. La liste
 * omet les modèles dont l'organisation a déjà une copie non archivée.
 */
export function AssignerModeleDialog({
  organisationId,
  onFermer,
  onAssigne,
}: {
  organisationId: Id<"organisations">;
  onFermer: () => void;
  onAssigne: (id: Id<"scorecards">) => void;
}) {
  const idModele = useId();
  const idTitre = useId();
  // Copies déjà présentes, repérées par le slug (unique) de leur modèle.
  const scorecardsOrg = useQuery(api.scorecards.listerScorecards, {
    organisationId,
  });
  const tousModeles = useQuery(api.scorecards.listerModeles, {});
  const dejaAssignes = new Set(scorecardsOrg?.map((s) => s.modeleSlug));
  // Un modèle jamais publié n'a rien à copier : le serveur le refuse.
  const modeles =
    scorecardsOrg === undefined
      ? undefined
      : tousModeles?.filter(
          (m) => m.numeroVersionPubliee !== null && !dejaAssignes.has(m.slug),
        );
  const assigner = useMutation(api.scorecards.assignerModele);
  const [modeleId, setModeleId] = useState<Id<"scorecards"> | "">("");
  const [titre, setTitre] = useState("");
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  const valider = async () => {
    if (!modeleId) return;
    setEnCours(true);
    setErreur(null);
    try {
      onAssigne(
        await assigner({
          modeleId,
          organisationId,
          titre: titre.trim() || undefined,
        }),
      );
    } catch (e) {
      setErreur(messageErreur(e));
      setEnCours(false);
    }
  };

  return (
    <Dialog isOpen onClose={onFermer} title="Assigner un modèle" className="max-w-lg">
      {modeles === undefined ? (
        <div className="flex justify-center p-8">
          <Loader />
        </div>
      ) : (
        <form
          className="space-y-4"
          onSubmit={(e) => {
            e.preventDefault();
            void valider();
          }}
        >
          <p className="text-sm text-muted-foreground">
            L'organisation reçoit une copie indépendante de la version publiée du
            modèle, publiée d'emblée : la modifier ne change pas le modèle, et
            modifier le modèle ne la change pas.
          </p>
          <div>
            <Label htmlFor={idModele} className="mb-1">
              Modèle
            </Label>
            <Select
              value={modeleId}
              onValueChange={(v) => setModeleId(v as Id<"scorecards">)}
            >
              <SelectTrigger
                id={idModele}
                className="w-full"
                disabled={modeles.length === 0}
              >
                <SelectValue
                  placeholder={
                    modeles.length === 0
                      ? "Tous les modèles publiés sont déjà assignés"
                      : "Choisir un modèle"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {modeles.map((m) => (
                  <SelectItem key={m._id} value={m._id}>
                    {m.titre}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor={idTitre} className="mb-1">
              Titre de la copie (facultatif)
            </Label>
            <Input
              id={idTitre}
              value={titre}
              placeholder="Titre du modèle par défaut"
              onChange={(e) => setTitre(e.target.value)}
            />
          </div>
          {erreur && (
            <p role="alert" className="text-sm text-destructive">
              {erreur}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="ghost" onClick={onFermer}>
              Annuler
            </Button>
            <Button type="submit" disabled={!modeleId || enCours}>
              Assigner
            </Button>
          </div>
        </form>
      )}
    </Dialog>
  );
}
